import { useState, useEffect } from 'react';

const HISTORY_KEY = 'color_history_v1';
const MAX_HISTORY = 20;

export interface HistoryItem {
    hex: string;
    timestamp: number;
}

export function useColorHistory() {
    const [history, setHistory] = useState<HistoryItem[]>([]);

    useEffect(() => {
        const saved = localStorage.getItem(HISTORY_KEY);
        if (saved) {
            setHistory(JSON.parse(saved));
        }
    }, []);

    const addToHistory = (hex: string) => {
        const newHistory = [
            { hex, timestamp: Date.now() },
            ...history.filter(h => h.hex.toUpperCase() !== hex.toUpperCase())
        ].slice(0, MAX_HISTORY);

        setHistory(newHistory);
        localStorage.setItem(HISTORY_KEY, JSON.stringify(newHistory));
    };

    const clearHistory = () => {
        setHistory([]);
        localStorage.removeItem(HISTORY_KEY);
    };

    return { history, addToHistory, clearHistory };
}

// Renk körlüğü simülasyonu
export function simulateColorBlindness(hex: string, type: 'protanopia' | 'deuteranopia' | 'tritanopia' | 'achromatopsia') {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;

    let newR = r, newG = g, newB = b;

    switch (type) {
        case 'protanopia': // Kırmızı körlüğü
            newR = 0.567 * r + 0.433 * g;
            newG = 0.558 * r + 0.442 * g;
            newB = 0.242 * g + 0.758 * b;
            break;
        case 'deuteranopia': // Yeşil körlüğü
            newR = 0.625 * r + 0.375 * g;
            newG = 0.7 * r + 0.3 * g;
            newB = 0.3 * g + 0.7 * b;
            break;
        case 'tritanopia': // Mavi körlüğü
            newR = 0.95 * r + 0.05 * g;
            newG = 0.433 * g + 0.567 * b;
            newB = 0.475 * g + 0.525 * b;
            break;
        case 'achromatopsia': // Tam renk körlüğü
            const gray = 0.299 * r + 0.587 * g + 0.114 * b;
            newR = newG = newB = gray;
            break;
    }

    const toHex = (val: number) => {
        const clamped = Math.max(0, Math.min(1, val));
        return Math.round(clamped * 255).toString(16).padStart(2, '0');
    };

    return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`.toUpperCase();
}

// Kontrast oranı hesaplama (WCAG)
export function getContrastRatio(hex1: string, hex2: string): number {
    const getLuminance = (hex: string) => {
        const r = parseInt(hex.slice(1, 3), 16) / 255;
        const g = parseInt(hex.slice(3, 5), 16) / 255;
        const b = parseInt(hex.slice(5, 7), 16) / 255;

        const toLinear = (c: number) => c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);

        return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
    };

    const l1 = getLuminance(hex1);
    const l2 = getLuminance(hex2);
    const lighter = Math.max(l1, l2);
    const darker = Math.min(l1, l2);

    return (lighter + 0.05) / (darker + 0.05);
}

// URL'den renk al
export function getColorFromURL(): string | null {
    const params = new URLSearchParams(window.location.search);
    const color = params.get('color');
    if (color && /^[0-9A-Fa-f]{6}$/.test(color)) {
        return `#${color.toUpperCase()}`;
    }
    return null;
}

// URL'e renk ekle
export function setColorToURL(hex: string) {
    const color = hex.replace('#', '');
    const url = new URL(window.location.href);
    url.searchParams.set('color', color);
    window.history.replaceState({}, '', url.toString());
}

// Complementary (tamamlayıcı) renk
export function getComplementaryColor(hex: string): string {
    const r = 255 - parseInt(hex.slice(1, 3), 16);
    const g = 255 - parseInt(hex.slice(3, 5), 16);
    const b = 255 - parseInt(hex.slice(5, 7), 16);
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`.toUpperCase();
}

// Analogous (benzer) renkler
export function getAnalogousColors(hex: string): string[] {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);

    // HSL'e çevir
    const rNorm = r / 255, gNorm = g / 255, bNorm = b / 255;
    const max = Math.max(rNorm, gNorm, bNorm);
    const min = Math.min(rNorm, gNorm, bNorm);
    let h = 0, s = 0, l = (max + min) / 2;

    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case rNorm: h = ((gNorm - bNorm) / d + (gNorm < bNorm ? 6 : 0)) / 6; break;
            case gNorm: h = ((bNorm - rNorm) / d + 2) / 6; break;
            case bNorm: h = ((rNorm - gNorm) / d + 4) / 6; break;
        }
    }

    // HSL'den HEX'e çevir
    const hslToHex = (h: number, s: number, l: number) => {
        const hue2rgb = (p: number, q: number, t: number) => {
            if (t < 0) t += 1;
            if (t > 1) t -= 1;
            if (t < 1 / 6) return p + (q - p) * 6 * t;
            if (t < 1 / 2) return q;
            if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
            return p;
        };

        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        const r = Math.round(hue2rgb(p, q, h + 1 / 3) * 255);
        const g = Math.round(hue2rgb(p, q, h) * 255);
        const b = Math.round(hue2rgb(p, q, h - 1 / 3) * 255);

        return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`.toUpperCase();
    };

    return [
        hslToHex((h - 0.083 + 1) % 1, s, l), // -30 derece
        hslToHex((h + 0.083) % 1, s, l),     // +30 derece
    ];
}
